import { tap, finalize } from 'rxjs';
import { BasePagination } from '@/app/shared/consts/pagination.const';
import {
  UsersApiService,
  UserCreatePayloadInterface,
  UserUpdatePayloadInterface,
  UserUpdatePasswordPayloadInterface,
  UserSocialLinksUpdatePayloadInterface,
} from '@/app/features/users';

import { Params } from '@angular/router';
import { signal, Injectable, WritableSignal } from '@angular/core';

import { UserInterface } from '@/app/shared/interfaces/user.interface';
import { PaginationInterface } from '@/app/shared/interfaces/response.interface';

@Injectable({
  providedIn: 'root',
})
export class UsersStore {
  public isLoading: WritableSignal<boolean> = signal(false);

  public users: WritableSignal<UserInterface[]> = signal([]);

  public usersPagination: WritableSignal<PaginationInterface> = signal(BasePagination);

  constructor(private apiService: UsersApiService) {}

  public loadUsers(params: Params = {}) {
    this.isLoading.set(true);

    return this.apiService.loadUsers(params).pipe(
      tap((response) => {
        this.users.set(response.payload.content);
        this.usersPagination.set(response.payload.pagination);
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }

  public loadResellers() {
    const params = {
      admin_type: 'reseller',
      per_page: 99999,
    };
    return this.apiService.loadUsers(params);
  }

  public loadUser(userId: number) {
    this.isLoading.set(true);

    return this.apiService.loadUser(userId).pipe(finalize(() => this.isLoading.set(false)));
  }

  public createUser(payload: UserCreatePayloadInterface) {
    this.isLoading.set(true);

    return this.apiService.createUser(payload).pipe(
      tap((response) => {
        this.users.update((users) => [...users, response.payload.content]);

        this.usersPagination.update((pagination) => {
          return {
            ...pagination,
            elements: pagination.elements + 1,
          };
        });
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }

  public bulkDeleteUsers(ids: number[] | 'all') {
    this.isLoading.set(true);

    const params = {
      id: Array.isArray(ids) ? ids.join(',') : ids,
    };

    return this.apiService.bulkDeleteUsers(params).pipe(finalize(() => this.isLoading.set(false)));
  }

  public deleteUser(id: number) {
    this.isLoading.set(true);

    return this.apiService.deleteUser(id).pipe(
      tap(() => {
        this.users.update((users) => {
          return users.filter((user) => user.id !== id);
        });

        this.usersPagination.update((pagination) => {
          return {
            ...pagination,
            elements: pagination.elements - 1,
          };
        });
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }

  public updateUser(id: number, payload: UserUpdatePayloadInterface | UserUpdatePasswordPayloadInterface) {
    this.isLoading.set(true);

    return this.apiService.updateUser(id, payload).pipe(
      tap((response) => {
        this.users.update((users) => {
          return users.map((user) => (user.id === id ? response.payload.content : user));
        });
      }),

      finalize(() => this.isLoading.set(false)),
    );
  }

  public loadFilters(params: Params = {}) {
    return this.apiService.loadFilters(params);
  }

  public updateUserAvatar(id: number, avatar: File | null = null) {
    return this.apiService.updateUserAvatar(id, avatar);
  }

  public updateSocialLinks(id: number, socials: UserSocialLinksUpdatePayloadInterface[]) {
    return this.apiService.updateSocialLinks(id, socials);
  }

  public exportUsers(params: Params = {}) {
    return this.apiService.exportUsers(params);
  }
}
